import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createMemoryPreferencesStore, type PreferencesStore } from './PreferencesStore';

const PreferencesContext = createContext<PreferencesStore | null>(null);

let fallbackStore: PreferencesStore | undefined;

export interface PreferencesProviderProps {
  store: PreferencesStore;
  children?: ReactNode;
}

/** Supplies the store `usePreference` reads from. The app decides the namespace, the backend and the mirror. */
export function PreferencesProvider({ store, children }: PreferencesProviderProps) {
  return <PreferencesContext.Provider value={store}>{children}</PreferencesContext.Provider>;
}

/**
 * The current store. Outside a provider this is a process-wide memory store, so a component lab or a
 * test renders without wiring one up; nothing is persisted there.
 */
export function usePreferencesStore(): PreferencesStore {
  const store = useContext(PreferencesContext);
  if (store) return store;
  fallbackStore ??= createMemoryPreferencesStore();
  return fallbackStore;
}

export interface PreferenceMeta {
  /** True until the store has answered the first read for this key. */
  loading: boolean;
  /** Removes the stored value; the hook returns to `fallback`. */
  remove: () => Promise<void>;
}

export type PreferenceSetter<T> = (next: T | ((previous: T) => T)) => Promise<void>;

/**
 * One preference as state: `[value, setValue, { loading, remove }]`.
 *
 * The first render uses the store's synchronous read when it has one, so a locally mirrored preference
 * never flashes its fallback. Writes are optimistic — the returned value changes at once and the store
 * catches up — and a write from another tab (or another subscriber) updates this one.
 */
export function usePreference<T>(key: string, fallback: T): [T, PreferenceSetter<T>, PreferenceMeta] {
  const store = usePreferencesStore();
  const [stored, setStored] = useState<{ key: string; value: T | undefined; resolved: boolean }>(() => {
    const sync = store.readSync?.<T>(key);
    return { key, value: sync, resolved: sync !== undefined };
  });
  const latest = useRef(stored);
  latest.current = stored;
  // Bumped on every write and every subscription update, so a slow first read that resolves after a
  // newer value has arrived (an optimistic write, another tab) cannot overwrite it.
  const version = useRef(0);

  useEffect(() => {
    let active = true;
    const sync = store.readSync?.<T>(key);
    setStored({ key, value: sync, resolved: sync !== undefined });
    if (sync === undefined) {
      const started = version.current;
      void store.get<T>(key).then((value) => {
        if (active && version.current === started) setStored({ key, value, resolved: true });
      });
    }
    const off = store.subscribe(key, (value) => {
      if (!active) return;
      version.current += 1;
      setStored({ key, value: value as T | undefined, resolved: true });
    });
    return () => {
      active = false;
      off();
    };
  }, [store, key]);

  const setValue = useCallback<PreferenceSetter<T>>(
    async (next) => {
      const current = latest.current.key === key ? latest.current.value : undefined;
      const resolvedNext =
        typeof next === 'function' ? (next as (previous: T) => T)(current === undefined ? fallback : current) : next;
      version.current += 1;
      setStored({ key, value: resolvedNext, resolved: true });
      await store.set(key, resolvedNext);
    },
    [store, key, fallback],
  );

  const remove = useCallback(async () => {
    version.current += 1;
    setStored({ key, value: undefined, resolved: true });
    await store.remove(key);
  }, [store, key]);

  const current = stored.key === key ? stored : { value: undefined, resolved: false };
  const value = current.value === undefined ? fallback : current.value;
  const meta = useMemo<PreferenceMeta>(() => ({ loading: !current.resolved, remove }), [current.resolved, remove]);
  return [value, setValue, meta];
}
